import { datadogLogError } from '#app/shared/utils/datadog'
import React, { type ErrorInfo, type PropsWithChildren } from 'react'
import { AppFatalPage } from './AppFatalPage'

interface State {
  hasError: boolean
}

/**
 * Application-level error boundary.
 */
export class AppErrorBoundary extends React.Component<
  PropsWithChildren,
  State
> {
  public state: State = {
    hasError: false
  }

  public static getDerivedStateFromError(_: Error): State {
    // Update state so the next render will show the fallback UI.
    return { hasError: true }
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Must be logged to console at the application level, as the Application
    // probably isn't initialized correctly.
    console.error('Uncaught error:', error, errorInfo)

    // Also attempt to report to DataDog
    // This might fail if DataDog isn't initialized yet, but it's worth trying
    try {
      datadogLogError(error, errorInfo)
    } catch (datadogError) {
      console.warn('Failed to report error to DataDog:', datadogError)
    }
  }

  public render() {
    if (this.state.hasError) {
      return <AppFatalPage />
    }

    return <>{this.props.children}</>
  }
}

export default AppErrorBoundary
